/**
 * @file history-coordinator.ts
 * @description Handles grouping actions and history manager extensions for the UI.
 */

import { HistoryManager, historyManager as coreHM, g } from '@hcie/core';
import { EventBus } from '@hcie/shared';

class GroupAction {
    constructor(public description: string, public actions: any[]) {}
    undo() { 
        for (let i = this.actions.length - 1; i >= 0; i--) {
            this.actions[i].undo();
        }
    }
    redo() { 
        for (let i = 0; i < this.actions.length; i++) {
            this.actions[i].redo();
        }
    }
}

/**
 * Extends a HistoryManager instance with grouping capabilities.
 */
export function extendHistoryManager(hm: any) {
    if (hm._grouped) return;
    hm._grouped = true;
    hm._groupStack = [];
    hm._currentGroup = null;

    const originalPush = hm.push;
    hm.push = function(action: any) {
        if (this._currentGroup) {
            this._currentGroup.actions.push(action);
        } else {
            originalPush.call(this, action);
            emitHistoryChanged(this);
        }
    };

    hm.beginGroup = function(description = 'Çoklu İşlem') {
        const group = { description, actions: [] };
        this._groupStack.push(group);
        this._currentGroup = group;
    };

    hm.endGroup = function() {
        if (this._groupStack.length === 0) return;
        const group = this._groupStack.pop();
        this._currentGroup = this._groupStack.length > 0 ? this._groupStack[this._groupStack.length - 1] : null;
        
        if (group && group.actions.length > 0) {
            if (group.actions.length === 1) {
                this.push(group.actions[0]);
            } else {
                originalPush.call(this, new GroupAction(group.description, group.actions));
                emitHistoryChanged(this);
            }
        }
        if ((window as any).updateUndoRedoUI) (window as any).updateUndoRedoUI();
    };

    // Add undo/redo listeners to emit events
    const originalUndo = hm.undo;
    hm.undo = function() {
        originalUndo.call(this);
        emitHistoryChanged(this);
    };

    const originalRedo = hm.redo;
    hm.redo = function() {
        originalRedo.call(this);
        emitHistoryChanged(this);
    };
}

const docHistories = new Map<string, HistoryManager>();

/**
 * Gets or creates a HistoryManager for a specific document.
 */
export function getHistoryForDoc(docId: string): HistoryManager {
    if (!docHistories.has(docId)) {
        const hm = new HistoryManager(g.max_undo_steps || 200);
        extendHistoryManager(hm);
        docHistories.set(docId, hm);
    }
    return docHistories.get(docId)!;
}

export function patchWindowHistoryManager() {
    const hm = (window as any).historyManager;
    if (!hm) {
        // Retry later if not yet available
        setTimeout(patchWindowHistoryManager, 200);
        return;
    }
    if (hm._uiPatched) return;
    hm._uiPatched = true;

    // If it's the core singleton we already extended, just wrap push/undo/redo to ensure signal
    // Most likely it's a bridge to historyManager singleton OR the one from main.bundle.js
    
    const origPush = hm.push.bind(hm);
    const origUndo = hm.undo.bind(hm);
    const origRedo = hm.redo.bind(hm);

    hm.push = function(action: any) {
        origPush(action);
        emitHistoryChanged(hm);
    };
    hm.undo = function() {
        origUndo();
        emitHistoryChanged(hm);
    };
    hm.redo = function() {
        origRedo();
        emitHistoryChanged(hm);
    };
}

/**
 * Renders the history list in the #history-pane .history-list element.
 * Fallback for when window.updateHistoryUI is missing.
 */
function updateHistoryList(actions: any[], currentIndex: number) {
    const pane = document.getElementById('history-pane');
    if (!pane) return;
    const list = pane.querySelector('.history-list');
    if (!list) return;

    if (actions.length > 0) {
        console.log(`[History-UI] Rendering ${actions.length + 1} items (including Open). Current Index: ${currentIndex}`);
    }

    list.innerHTML = '';

    // Always show initial "Open" state
    const openItem = document.createElement('div');
    openItem.className = 'history-item' + (currentIndex < 0 ? ' active' : '');
    openItem.innerHTML = `
        <div class="history-item-icon">📝</div>
        <div class="history-item-text">Open</div>
    `;
    openItem.onclick = () => {
        const hm = (window as any).historyManager;
        if (hm && typeof hm.jumpTo === 'function') hm.jumpTo(-1);
    };
    list.appendChild(openItem);

    actions.forEach((action, i) => {
        const item = document.createElement('div');
        const isGhosted = i > currentIndex;
        const isActive = i === currentIndex;
        
        item.className = 'history-item' + (isActive ? ' active' : '') + (isGhosted ? ' ghosted' : '');
        if (isGhosted) {
             item.style.opacity = '0.4'; // A bit lighter then 0.5 to show it's ghosted
        }

        let icon = '⚡';
        const desc = action.description || '';
        if (desc.includes('Pen') || desc.includes('Brush') || desc.includes('Draw')) icon = '✏️';
        else if (desc.includes('Layer')) icon = '📄';
        else if (desc.includes('Vector')) icon = '🔷';
        else if (desc.includes('Add')) icon = '➕';
        else if (desc.includes('Red Eye') || desc.includes('Eye')) icon = '👁️';

        item.innerHTML = `
            <div class="history-item-icon">${icon}</div>
            <div class="history-item-text">${desc}</div>
        `;

        item.onclick = () => {
            const hm = (window as any).historyManager;
            if (hm && typeof hm.jumpTo === 'function') hm.jumpTo(i);
        };

        list.appendChild(item);
    });
}

function emitHistoryChanged(hm: any) {
    // Collect actions from various potential HistoryManager formats
    const actions = hm.currentActions ?? hm._actions ?? hm.actions ?? [];
    const idx = hm.currentIdx ?? hm._currentIndex ?? hm.currentIndex ?? -1;
    
    const payload = {
        actions: actions.map((a: any, i: number) => ({
            description: a.description,
            active: i === idx,
            ghosted: i > idx
        })),
        currentIndex: idx
    };

    EventBus.emit('HISTORY_CHANGED', payload);
    
    // Fallback UI update
    updateHistoryList(actions, idx);
    
    if ((window as any).updateUndoRedoUI) (window as any).updateUndoRedoUI();
}
